import { useTranslation } from 'react-i18next';
import React, { memo, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import cx from 'clsx';

import {
  UserCardPopup,
  VipLogoWithGift,
  HelperTooltip,
  RichTextView,
  UnionAvatar,
} from '@/components';
import { SuicideTag } from '@/views/Profile/components/SuicideTag';
import { useDeviceTypeIsMobile } from '@/hooks';
import { getOfficialPath, openNewWindow, isOfficalAddress } from '@/utils';

import { FeedArticle } from '../../types';
import style from './index.module.scss';

type Props = {
  article: FeedArticle;
  className?: string;
  maxDisplay?: number;
  showRawImage?: boolean;
  isDetails: boolean;
  maxImgSize?: [number, number];
  enableTranslate?: boolean;
  restParams?: Record<string, string | number | boolean | undefined>;
  // 这个被引用的 article 只有一张图片，且外面没有图片
  onlyOriginImg?: boolean;
  disableDetailNav?: boolean;
};

const DetailMaxImageSize: [number, number] = [633, 510];
const MobileDetailMaxImageSize: [number, number] = [window.screen.width - 72, 260];
const ListMaxImageSize: [number, number] = [558, 510];
const MobileListMaxImageSize: [number, number] = [window.screen.width - 118, 260];
const SmallMaxImageSize: [number, number] = [100, 100];

export const QuotedArticle = memo((props: Props) => {
  const {
    className,
    restParams,
    article,
    isDetails,
    maxDisplay,
    enableTranslate,
    showRawImage,
    onlyOriginImg,
    disableDetailNav,
  } = props;
  const history = useHistory();
  const isMobile = useDeviceTypeIsMobile();

  const handleUserClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (disableDetailNav) {
        return;
      }

      const userId = e.currentTarget?.dataset?.userId || article.creator_id;
      const isOffical = isOfficalAddress(userId);
      // path上officialId不带0a；

      openNewWindow(
        isOffical
          ? `${getOfficialPath(article?.creator as OfficialAccount)}/stream`
          : `/profile/${userId}/stream`,
        '_blank',
      );
    },
    [article, disableDetailNav],
  );

  const handleQuoteClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (disableDetailNav) {
        return;
      }

      history.push(`/stream/${article.id!}`);
    },
    [disableDetailNav, history, article.id],
  );

  return article.is_withdrawn ? (
    <EmptyQuotedArticle />
  ) : (
    <div
      className={cx(style.quoteArticle, disableDetailNav && style.isDisableNav, className)}
      onClick={handleQuoteClick}>
      <div className={style.creator}>
        <UnionAvatar
          size={28}
          data={article.creator}
          className={style.quoteAvatar}
          userCardPopup={{
            restParams,
          }}
          onClick={handleUserClick}
        />
        <UserCardPopup restParams={restParams} address={article.creator?.id || ''}>
          <div onClick={handleUserClick} className={style.uid}>
            {(article.creator as UserInfo)?.is_suicide ? (
              <SuicideTag userAddress={article.creator?.id!} />
            ) : (
              article._author
            )}
          </div>
        </UserCardPopup>
        {article._showVip ? (
          <VipLogoWithGift userInfo={article.creator as UserInfo} marginRight={4} />
        ) : null}
        <HelperTooltip title={article._fulltime} placement="topLeft">
          <div className={style.createdAt}>{`· ${article._daytime}`}</div>
        </HelperTooltip>
      </div>
      <div className={style.content}>
        <RichTextView
          article={article}
          onClick={handleQuoteClick}
          maxDisplay={maxDisplay}
          showRawImage={showRawImage}
          maxImgSize={
            onlyOriginImg
              ? isDetails
                ? isMobile
                  ? MobileDetailMaxImageSize
                  : DetailMaxImageSize
                : isMobile
                  ? MobileListMaxImageSize
                  : ListMaxImageSize
              : SmallMaxImageSize
          }
          enableTranslate={enableTranslate}
          // 有些只有在 detail 显示的内容，就不让它显示了
          isDetails={false}
          contentClassName={cx(style.quoteContent, article.images?.length && style.withImage)}
          imageClassName={style.quoteImage}
          previewCardQuoted
        />
        <div className={style.mask} />
      </div>
    </div>
  );
});

export const EmptyQuotedArticle = memo(() => {
  const { t } = useTranslation();

  return (
    <div className={style.emptyQuotedArticle}>{t('ArticleContent_QuotedArticle.1lgqzes')}</div>
  );
});
